/** @jsx jsx */
import { jsx } from '@emotion/core';
import { FC } from 'react';
import ordinal from 'ordinal';

import {
  Box,
  Card as RebassCard,
  CardProps as RebassCardProps,
  Heading,
  Flex,
  Text
} from 'rebass';
import { Image } from '../../../ui-components/images/images';
import Links from '../../links/links';
import Link, { LinkProps } from '../../../ui-components/link/link';


export interface CardProps extends RebassCardProps {
  category: [];
  concepts: [];
  deviceOsSupport?: [];
  experienceQuarter: number;
  experienceYear: number;
  gradeLevel: [];
  id?: string;
  image?: any;
  length?: number;
  lessonNumber?: number;
  links: LinkProps;
  cardUrl: string;
  name?: string;
  practice?: [];
  skillsTaught?: [];
  summary: any;
  maxTextLength?: number;
  noMin?: boolean;
  imageOn?: 'top' | 'left' | 'right';
  secondaryHeader?: boolean;
}

const Card: FC<CardProps & JSX.IntrinsicElements['a']> = ({
  className = '',
  id = '',
  name,
  gradeLevel,
  experienceQuarter,
  experienceYear,
  lessonNumber,
  practice,
  concepts,
  summary,
  links,
  cardUrl,
  image,
  maxTextLength = 120,
  noMin = false,
  imageOn = 'top',
  secondaryHeader = true,
  ...rest
}) => {
  const imageOnToFlex = imageOn => {
    let flexDirection = 'row';
    if (imageOn === 'right') {
      flexDirection = 'row-reverse';
    }
    return flexDirection;
  };

  const linkChecker = (cardUrl) => {
    cardUrl = cardUrl[-1] === '/' ? cardUrl : cardUrl + `/`;
    return cardUrl;
  }

  cardUrl = linkChecker(cardUrl);

  return (
    <RebassCard
      key={id}
      as='a'
      href={cardUrl}
      variant="card"
      data-gtm-link-track={`website-lesson-plan--${cardUrl.replace('/', '')}`}
      sx={{
        ':hover': {
          boxShadow: '0px 6px 6px rgba(0, 0, 0, 0.25)'
        },
        position: 'relative',
        display: 'flex',
        flexDirection:
          imageOn === 'left' || imageOn === 'right'
            ? imageOnToFlex(imageOn)
            : 'column',
        textDecoration: 'none',
        overflow: 'hidden'
      }}
      {...rest}
    >
      <Flex maxWidth='100%'>
        <Image
          src={image}
          style={{
            flexShrink: 0,
            maxHeight: '200px',
            maxWidth: '100%',
            margin: 'auto 0'
          }}
        />
      </Flex>
      <Flex p={3} flexDirection="column" height="100%" color="text">
        {name && (
          <Heading
            variant="headline"
            mb="3"
            fontSize='16px !important'
            color={secondaryHeader ? 'secondary' : 'text'}
          >
            #{lessonNumber} {name}
          </Heading>
        )}
        {experienceQuarter && experienceYear && (
          <Box mb={4} sx={{ hyphens: 'auto' }}>
            {gradeLevel && (
              <Text>
                <strong>Grade:</strong> {gradeLevel}
              </Text>
            )}
            <Text>
              <strong>Experience:</strong> {ordinal(experienceYear)} year,{' '}
              {ordinal(experienceQuarter)} quarter
            </Text>
          </Box>
        )}
        {summary && summary.summary && (
          <Text mb={4} sx={{ hyphens: 'auto' }}>
            {summary.summary}
          </Text>
        )}
      </Flex>
    </RebassCard>
  );
};

export default Card;
